import { usePropertyBuilder } from "@/components/views/config/helpers";
import { type ModTag } from "@/components/views/mods";
import { useSteamWorkshop } from "@/components/views/mods/steam_workshop";
import type { SteamWorkshopItem, WindroseProfileNamespace } from "@/graphql";
import { i18n } from "@/i18n";
import { useServiceStore } from "@/store/service";
import { useRouteUtils } from "@/util/route";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faBox, faCode, faPuzzlePiece, faScroll, faSliders } from "@fortawesome/pro-solid-svg-icons";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import defaultProfile, { asyncRoute } from "./default";
import { commonMenuItems } from "./common";
import { MenuItem } from "./types";
import type { ConfigSection, GameProfile, Menu, ProfileCtx } from "./types";

const { t } = i18n.global;

// virtual.mods stores source-prefixed repo keys (TR-4891). Windrose has one mod tab per source, each
// owning only its own prefix — entries of the other source pass through its operations untouched.
// The shared Mods.vue page and this composable resolve the active source from the tab route.
interface ModSourceConfig {
  prefix: string;
  storeTitleKey: string;
}

// Nexus is the default tab: it carries by far the most Windrose mods, and CurseForge blocks API
// downloads for most of its own (those never reach the browser at all).
const DEFAULT_MOD_SOURCE_ROUTE = "nexus-mods";
const MOD_SOURCE_CONFIGS: Record<string, ModSourceConfig> = {
  [DEFAULT_MOD_SOURCE_ROUTE]: {
    prefix: "modulus_nexusmods_windrose_",
    storeTitleKey: "ngp/mods/dialog/link/nexusmods",
  },
  "curseforge-mods": {
    prefix: "modulus_curseforge_windrose_",
    storeTitleKey: "ngp/mods/dialog/link/curseforge",
  },
};

// Mod functional type → tag. The names are proper nouns (not translated); the icon is decorative.
// `mod_type` is classified at ingestion from what the archive actually contains (TR-4891).
const MOD_TYPE_TAGS: Record<string, { label: string; icon: IconDefinition }> = {
  lua: { label: "Lua", icon: faScroll },
  cpp: { label: "C++", icon: faCode },
  pak: { label: "Pak", icon: faBox },
};

const windrosePlusMenuItem = new MenuItem(
  "windrose-plus",
  faSliders,
  asyncRoute(() => import("../components/views/config/windrose/WindrosePlusConf.vue")),
);

// Windrose mods are direct-placement and have no load order, so the manage page shows no reorder
// arrows (allowModsReordering is off) — but it is still where installed mods are removed.
const modsMenuItem = (route: string) =>
  new MenuItem(
    route,
    faPuzzlePiece,
    asyncRoute(() => import("../components/views/config/windrose/Mods.vue")),
    [
      {
        route: "manage",
        component: asyncRoute(() => import("../components/views/mods/manage/ModsManage.vue")),
      },
    ],
  );

const createMenu = (wpEnabled: boolean): Menu => {
  const menu = defaultProfile().menu;
  // menu[0].items = [status, settings, fileManager] — insert WP between settings and fileManager
  if (wpEnabled) {
    menu[0].items.splice(2, 0, windrosePlusMenuItem);
  }
  menu[0].items.push(commonMenuItems.logs);
  // The mod-source tabs get their own sidebar section between Settings and Administration.
  menu.splice(1, 0, { name: "modding", items: Object.keys(MOD_SOURCE_CONFIGS).map(modsMenuItem) });
  return menu;
};

export function useMods() {
  const { t: translate } = useI18n();
  const service = useServiceStore();
  const { buildProperty } = usePropertyBuilder();
  const { tabSegments } = useRouteUtils();

  // The Mods.vue page is shared across the source tabs; the active tab route selects which source
  // this composable operates on. The shared mod components (DialogManageMod/ModTags/ModDrag) resolve
  // useMods() during their own setup, always within the active tab, so the route is authoritative.
  const sourceConfig = MOD_SOURCE_CONFIGS[tabSegments.value[0]] ?? MOD_SOURCE_CONFIGS[DEFAULT_MOD_SOURCE_ROUTE];

  const profile = computed(() => service.ctx.profile as WindroseProfileNamespace | undefined);
  const selectedWorkshopItems = computed(() => profile.value?.selectedWorkshopItems ?? []);
  const subscribedFileIds = buildProperty<Array<string>>("virtual", "mods", []);
  const collectionsStub = computed({
    get() {
      return [];
    },
    set() {},
  });
  const workshop = useSteamWorkshop(
    selectedWorkshopItems,
    subscribedFileIds,
    collectionsStub,
    "",
    false,
    false,
    sourceConfig.prefix,
  );

  const storeTitle = computed(() => translate(sourceConfig.storeTitleKey));

  // isServer is tri-state: true = a server-side placement was recognised, false = client-only,
  // null = nothing classified yet. Only true is selectable — the backend mutator drops everything
  // else from targetPackages on save anyway, so make that visible up front.
  const isModAddable = (mod: SteamWorkshopItem) => mod.isServer === true;

  // Both Windrose sources derive the flag per mod, so the badge stays meaningful on every tab.
  const showClientModFlag = (mod: SteamWorkshopItem) => Boolean(mod.requiresClientMod);

  const modTags = (mod: SteamWorkshopItem): ModTag[] => {
    const tags: ModTag[] = [];
    // Mod type (Lua / C++ / Pak) — proper names, no translation needed.
    const typeTag = mod.modType ? MOD_TYPE_TAGS[mod.modType] : undefined;
    if (typeTag) {
      tags.push(typeTag);
    }
    // Loader hint: this mod needs the UE4SS runtime enabled.
    if (mod.requiresUe4ss) {
      tags.push({ label: translate("ngp/windrose/mods/tag/requires_ue4ss"), icon: faPuzzlePiece });
    }
    // No "Server mod" tag: everything the browse view lists is already server-installable
    // (it filters on is_server). The client-only / pending states only surface in the installed
    // list, where a mod's metadata may have changed after selection.
    if (mod.isServer === false) {
      tags.push(translate("ngp/windrose/mods/tag/client_only"));
    } else if (mod.isServer !== true) {
      tags.push(translate("ngp/windrose/mods/tag/pending"));
    } else if (showClientModFlag(mod)) {
      tags.push(translate("ngp/windrose/mods/tag/requires_client_mod"));
    }
    return tags;
  };

  const modHint = (mod: SteamWorkshopItem) => {
    if (mod.isServer === false) {
      return translate("ngp/windrose/mods/hint/client_only");
    }
    if (mod.isServer !== true) {
      return translate("ngp/windrose/mods/hint/pending");
    }
    if (showClientModFlag(mod)) {
      return translate("ngp/windrose/mods/hint/requires_client_mod");
    }
    return undefined;
  };

  return { ...workshop, storeTitle, modTags, isModAddable, modHint, showClientModFlag };
}

const configSections: ConfigSection[] = [
  { component: "ConfSection", props: { name: "basic" } },
  { component: "ConfSection", props: { name: "world" } },
  { component: "WindroseCustomWorldSettings", props: { name: "custom_world_settings" } },
  { component: "ConfSection", props: { name: "modding" } },
];

const join = () => {
  window.open(t("ngp/settings/section/windrose/link"), "_blank", "noopener,noreferrer");
};

export default (ctx: ProfileCtx): Partial<GameProfile> => {
  const profile = ctx.profile?.__typename === "WindroseProfileNamespace" ? ctx.profile : undefined;
  const useDirectConnection = profile?.useDirectConnection ?? false;
  const wpEnabledRaw = ctx.profile?.publicConfigs?.virtual?.windrose_plus_enabled;
  const wpEnabled = wpEnabledRaw === true || wpEnabledRaw === "true";

  return {
    menu: createMenu(wpEnabled),
    configSections,
    hasActivePlayers: ctx.profile?.publicConfigs?.virtual?.ue4ss_enabled === "true",
    hideHeaderIpPort: !useDirectConnection,
    join: {
      buttonTextKey: "ngp/settings/section/windrose",
      joinFn: join,
    },
    dynamicSection: {
      controllerConfig: "virtual",
      controllerField: "active_world",
      targetConfigPrefix: "world_",
      targetConfigAlias: "world",
    },
    useMods,
  };
};
